import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import useBreakpoint from "use-breakpoint";
import { SkeletonLoader } from "@hubertai/hubert-component-library";
import { fetchApplications } from "../../../behavior/api/application";
import { PageRoute } from "../../../constants";
import type Application from "../../../types/application";
import { BREAKPOINTS } from "../../../utils/constants/breakpoints";
import { Alert, Button, Heading, HorizontalLine } from "../../elements";
import InterviewBox from "./InterviewBox";
import InterviewsTable from "./InterviewsTable";

interface YourInterviewsTableProps {
    forDashboard?: boolean;
}

const YourInterviewsTable = ({ forDashboard = false }: YourInterviewsTableProps) => {
    const { t } = useTranslation();
    const { breakpoint } = useBreakpoint(BREAKPOINTS, "sm");
    const isLayoutChange = breakpoint === "sm" || breakpoint === "md";
    const limit = forDashboard ? 4 : 1000;
    const skip = 0;
    const { data, isLoading: loading } = useQuery({
        queryKey: ["applications", { limit, skip }],
        queryFn: () => fetchApplications(limit, skip),
    });
    const yourInterviews: Application[] = data?.nodes ?? [];

    const NoInterviews = (
        <div className="mt-2">
            <Alert type="info">{t("Common.MyInterviews.NoInterviews_Message")}</Alert>
        </div>
    );

    return (
        <div className="your-interviews-table-wrapper">
            <div className="title-section">
                <Heading level="h5">{t("Common.MyInterviews.Title")}</Heading>
                {forDashboard && (
                    <Button size="small" secondary link to={PageRoute.INTERVIEWS}>
                        <div>{t("Common.MyInterviews.ButtonText_SeeAllInterviews")}</div>
                        <ArrowRight size={20} className="ms-1.5 rtl:-scale-x-100" />
                    </Button>
                )}
            </div>
            <HorizontalLine weight="semi-dark" />
            {loading && <SkeletonLoader count={3} />}
            {!isLayoutChange && !loading && yourInterviews.length > 1 ? (
                <InterviewsTable yourInterviews={yourInterviews} loading={loading} />
            ) : (
                yourInterviews &&
                !loading &&
                yourInterviews.map((row) => <InterviewBox key={row.id} row={row} loading={loading} />)
            )}
            {!loading && yourInterviews.length === 0 && NoInterviews}
        </div>
    );
};

export default YourInterviewsTable;
